import moment from "moment";
import { AmplitudeCategories, createTrackerForCategory } from "./amplitude";
import { WorkspacePlanTier } from "common/interfaces";

const trackEvent = createTrackerForCategory(AmplitudeCategories.ModifyWorkspacePlan);

enum events {
    CancelFlowStart = "CancelFlowStart",
    CancelFlowComplete = "CancelFlowComplete",
    EnterpriseInquiry = "EnterpriseInquiry",
    ManagePlanDialogOpen = "ManagePlanDialogOpen",
    ReactivateFlowStart = "ReactivateFlowStart",
    ReactivateFlowComplete = "ReactivateFlowComplete",
    UpgradeFlowStart = "UpgradeFlowStart",
    UpgradeFlowComplete = "UpgradeFlowComplete",
    StartSubscriptionEarly = "StartSubscriptionEarly"
}

let _BillingControllerPromise: Promise<any>;
const getBillingController = () => {
    if (!_BillingControllerPromise) {
        // @ts-ignore
        _BillingControllerPromise = import("js/controllers/BillingController").then(module => module.default);
    }
    return _BillingControllerPromise;
};

let _WorkspaceControllerPromise: Promise<any>;
const getWorkspaceController = () => {
    if (!_WorkspaceControllerPromise) {
        // @ts-ignore
        _WorkspaceControllerPromise = import("js/controllers/WorkspaceController").then(module => module.default);
    }
    return _WorkspaceControllerPromise;
};

function getFullDaysBetween(startTimestamp:number, endTimestamp:number) {
    const startDate = moment(startTimestamp).startOf("day");
    const endDate = moment(endTimestamp).startOf("day");
    return endDate.diff(startDate, "days");
}

export const ModifyWorkspacePlan: Record<events, (...args: any[]) => void> = {
    CancelFlowStart: ()=>{
        // Do we need to identify if they are in a trial or not?
        const eventProperties : { } = {};
        trackEvent(events.CancelFlowStart, eventProperties, { audit: true });
    },
    CancelFlowComplete: async ()=>{
        const billingController = await getBillingController();
        const billing_period_end = billingController.subscription.current_period_end * 1000;
        const days_to_period_end = getFullDaysBetween(moment().valueOf(), billing_period_end);

        const workspaceController = await getWorkspaceController();
        const workspace_age_days = getFullDaysBetween(workspaceController.workspace.createdAt, moment().valueOf());

        const eventProperties = { days_to_period_end, workspace_age_days };
        trackEvent(events.CancelFlowComplete, eventProperties, { audit: true });
    },
    EnterpriseInquiry: ()=>{
        const eventProperties : { } = {};
        trackEvent(events.EnterpriseInquiry, eventProperties, { audit: true });
    },
    ManagePlanDialogOpen: ({ featured_plan, analytics }: {featured_plan?: string, analytics?: { cta: string; }})=>{
        const eventProperties = {
            ...(featured_plan && { featured_plan }),
            ...(analytics?.cta && { cta: analytics.cta })
        };
        trackEvent(events.ManagePlanDialogOpen, eventProperties, { audit: true });
    },
    ReactivateFlowStart: () => {
        const eventProperties : { } = {};
        trackEvent(events.ReactivateFlowStart, eventProperties, { audit: true });
    },
    ReactivateFlowComplete: () => {
        const eventProperties : { } = {};
        trackEvent(events.ReactivateFlowComplete, eventProperties, { audit: true });
    },
    StartSubscriptionEarly: ({ days_to_trial_end })=>{
        const eventProperties = { days_to_trial_end };

        trackEvent(events.StartSubscriptionEarly, eventProperties, { audit: true });
    },
    UpgradeFlowStart: ()=>{
        const eventProperties : { } = {};
        trackEvent(events.UpgradeFlowStart, eventProperties, { audit: true });
    },
    UpgradeFlowComplete: async () => {
        const workspaceController = await getWorkspaceController();
        const upgrade_plan_to = workspaceController.plan.name;

        const billingController = await getBillingController();
        const quantity_seats_total = workspaceController.plan.tier === WorkspacePlanTier.TEAM ? billingController.paidSeatCount : null;

        const eventProperties = {
            upgrade_plan_to,
            ...(quantity_seats_total && { quantity_seats_total })
        };
        trackEvent(events.UpgradeFlowComplete, eventProperties, { audit: true });
    }
};
